import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Syringe, Bell, DollarSign } from 'lucide-react';
import Footer from '../components/Footer';
import { trackCalculatorInteraction, trackGetStarted, trackDemoRequestEnhanced } from '../utils/analytics';
import { usePageTitle } from '../hooks/usePageTitle';
import { APP_NAME } from '../config/branding';

const SolutionsGrow = () => {
  usePageTitle('Grow Revenue');
  
  const [numDoctors, setNumDoctors] = useState(2);
  const [email] = useState('');

  // Revenue Gain Logic - Vaccine reminders & preventive upsells
  // Conservative-to-moderate lift: $40k-$80k per doctor/year
  const revenuePerDoctorLow = 40000;
  const revenuePerDoctorMid = 60000;
  const revenuePerDoctorHigh = 80000;
  
  const revenueLow = revenuePerDoctorLow * numDoctors;
  const revenueMid = revenuePerDoctorMid * numDoctors;
  const revenueHigh = revenuePerDoctorHigh * numDoctors;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section - Same as Home */}
      <section className="pt-20 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-normal text-[#1a2b4a] mb-6 leading-tight" style={{
            fontFamily: 'Georgia, "Times New Roman", Times, serif',
          }}>
            Unlock hidden <span className="italic font-semibold" style={{ borderBottom: '4px solid #f97316', paddingBottom: '2px' }}>revenue</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed" style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          }}>
            Automated vaccine reminders and preventive care campaigns turn missed opportunities into booked appointments.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/signup"
              className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-green-700 transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-green-600/20"
              onClick={() => trackGetStarted('solutions-grow-hero')}
            >
              Start Growing Revenue
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={`https://calendly.com/justin-helppet/30min${email ? `?email=${encodeURIComponent(email)}` : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-green-700 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-50 transition-all inline-flex items-center justify-center gap-2 border border-gray-200"
              onClick={() => trackDemoRequestEnhanced('solutions-grow-hero', email)}
            >
              Request a demo
            </a>
          </div>

          {/* Hero Chart - Animated Line Chart */}
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-white p-8 md:p-12">
              {/* Big Number Display */}
              <div className="text-center mb-8">
                <span className="text-7xl md:text-8xl font-bold text-green-600 transition-all duration-300" style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
                  fontWeight: 700,
                }}>
                  ${(revenueMid / 1000).toFixed(0)}k
                </span>
                <p className="text-xl text-gray-500 mt-3">annual revenue with {numDoctors} vet{numDoctors > 1 ? 's' : ''}</p>
              </div>

              {/* Animated Line Chart - steep diagonal */}
              <div className="relative h-64 mb-6">
                <svg 
                  viewBox="0 0 100 80" 
                  className="w-full h-full"
                  preserveAspectRatio="none"
                >
                  {/* Gradient definition */}
                  <defs>
                    <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity="0.02" />
                    </linearGradient>
                  </defs>
                  
                  {/* Area fill - steep triangle from bottom-left to top-right */}
                  <polygon
                    points={`0,80 0,${75 - (numDoctors * 1)} 100,${80 - (numDoctors * 7.5)} 100,80`}
                    fill="url(#areaGradient)"
                    className="transition-all duration-300 ease-out"
                  />
                  
                  {/* Main growth line - STEEP diagonal */}
                  <line
                    x1="0"
                    y1={75 - (numDoctors * 1)}
                    x2="100"
                    y2={80 - (numDoctors * 7.5)}
                    stroke="#22c55e"
                    strokeWidth="0.6"
                    strokeLinecap="round"
                    className="transition-all duration-300 ease-out"
                  />
                </svg>
              </div>

              {/* Slider */}
              <div className="max-w-md mx-auto">
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={numDoctors}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    setNumDoctors(newValue);
                    trackCalculatorInteraction(newValue);
                  }}
                  className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-green-500"
                  style={{
                    background: `linear-gradient(to right, #22c55e 0%, #22c55e ${((numDoctors - 1) / 9) * 100}%, #e5e7eb ${((numDoctors - 1) / 9) * 100}%, #e5e7eb 100%)`
                  }}
                />
                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-400">1 vet</span>
                  <span className="text-2xl font-semibold text-gray-700">{numDoctors} veterinarian{numDoctors > 1 ? 's' : ''}</span>
                  <span className="text-sm text-gray-400">10 vets</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-white py-4 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="flex flex-wrap justify-center items-center gap-12 opacity-60">
              <div className="text-xl font-semibold text-gray-400">Vaccine Reminders</div>
              <div className="text-xl font-semibold text-gray-400">Wellness Campaigns</div>
              <div className="text-xl font-semibold text-gray-400">Preventive Care</div>
              <div className="text-xl font-semibold text-gray-400">Upsell Automation</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-normal text-[#1a2b4a] text-center mb-4" style={{
            fontFamily: 'Georgia, "Times New Roman", Times, serif',
          }}>
            How {APP_NAME} drives <span className="italic font-semibold">growth</span>
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-2xl mx-auto">
            Three automated systems working together to maximize your revenue.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <Syringe className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Vaccine Reminders</h3>
              <p className="text-gray-600 leading-relaxed">
                Automated multi-channel reminders (SMS, email, voice) bring pets back for overdue vaccinations.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <Bell className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Preventive Campaigns</h3>
              <p className="text-gray-600 leading-relaxed">
                Target pets due for wellness exams, dental cleanings, and senior screenings.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Wellness Upsells</h3>
              <p className="text-gray-600 leading-relaxed">
                Convert one-time visits into recurring wellness plan memberships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-normal text-[#1a2b4a] mb-4" style={{
              fontFamily: 'Georgia, "Times New Roman", Times, serif',
            }}>
              Calculate your <span className="italic font-semibold">potential</span>
            </h2>
            <p className="text-lg text-gray-600">
              Based on $40k-$80k revenue lift per veterinarian per year.
            </p>
          </div>

          {/* Calculator Card */}
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-200">
            
            {/* Slider Section */}
            <div className="mb-10">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Number of veterinarians</h3>
              <input
                type="range"
                min="1"
                max="10"
                value={numDoctors}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value);
                  setNumDoctors(newValue);
                  trackCalculatorInteraction(newValue);
                }}
                className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-green-500"
                style={{
                  background: `linear-gradient(to right, #22c55e 0%, #22c55e ${((numDoctors - 1) / 9) * 100}%, #e5e7eb ${((numDoctors - 1) / 9) * 100}%, #e5e7eb 100%)`
                }}
              />
              <div className="flex justify-between text-sm text-gray-400 mt-2">
                <span>1</span>
                <span className="text-4xl font-semibold text-green-600 -mt-2">{numDoctors}</span>
                <span>10</span>
              </div>
            </div>

            {/* Results Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Conservative</p>
                <p className="text-4xl font-bold text-gray-900">${(revenueLow / 1000).toFixed(0)}k</p>
                <p className="text-sm text-gray-400 mt-1">$40k/doctor</p>
              </div>
              <div className="bg-green-50 rounded-2xl p-6 border-2 border-green-200">
                <p className="text-sm text-green-600 font-medium mb-2">Moderate</p>
                <p className="text-4xl font-bold text-green-600">${(revenueMid / 1000).toFixed(0)}k</p>
                <p className="text-sm text-gray-400 mt-1">$60k/doctor</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <p className="text-sm text-gray-500 mb-2">High (Defensible)</p>
                <p className="text-4xl font-bold text-gray-900">${(revenueHigh / 1000).toFixed(0)}k</p>
                <p className="text-sm text-gray-400 mt-1">$80k/doctor</p>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-400 text-center mt-6">
            Based on vaccine reminders, preventive care upsells, and wellness plan conversions
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-green-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-normal text-white mb-6" style={{
            fontFamily: 'Georgia, "Times New Roman", Times, serif',
          }}>
            Ready to grow your practice?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Join hundreds of veterinary practices already seeing results.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-600 rounded-lg text-lg font-medium hover:bg-green-50 transition-colors"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/solutions/recover"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-lg text-lg font-medium hover:bg-green-700 transition-colors"
            >
              See No-Show Recovery
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolutionsGrow;
